import type {
  IReservation,
  TReservationSource,
  TReservationStatus,
} from '@/interfaces/IReservation';
import {
  defaultBoardDate,
  formatDayShort,
  formatTime,
} from './reservation-time';

export const RESERVATION_ALERT_TRIGGERS = [
  'created',
  'deposit_paid',
  'guest_late',
  'guest_cancelled',
  'guest_modified',
] as const;
export type TReservationAlertTrigger = typeof RESERVATION_ALERT_TRIGGERS[number];

const TRIGGERS = new Set<string>(RESERVATION_ALERT_TRIGGERS);
export function isReservationAlertTrigger(
  value: unknown
): value is TReservationAlertTrigger {
  return typeof value === 'string' && TRIGGERS.has(value);
}

const GUEST_SOURCES: readonly TReservationSource[] = ['online', 'waitlist'];
const BOOKED_STATUSES: readonly TReservationStatus[] = [
  'pending',
  'confirmed',
  'arrived',
  'seated',
];
const MODIFIABLE_STATUSES: readonly TReservationStatus[] = [
  'pending',
  'confirmed',
  'arrived',
];
const GUEST_LATE_MINUTES: readonly number[] = [10, 20, 30];

/** The toast message for an authorized row, or null when the row does not warrant one. */
export function reservationAlertMessageKey(
  reservation: Pick<
    IReservation,
    'status' | 'source' | 'cancelledBy' | 'guestLateMinutes'
  >,
  trigger: TReservationAlertTrigger
): string | null {
  switch (trigger) {
    case 'created':
    case 'deposit_paid':
      return GUEST_SOURCES.includes(reservation.source) &&
        BOOKED_STATUSES.includes(reservation.status)
        ? 'reservations.realtime.newOnline'
        : null;
    case 'guest_late':
      return GUEST_SOURCES.includes(reservation.source) &&
        reservation.status === 'confirmed' &&
        GUEST_LATE_MINUTES.includes(reservation.guestLateMinutes ?? 0)
        ? 'reservations.realtime.guestLate'
        : null;
    case 'guest_cancelled':
      return reservation.status === 'cancelled' &&
        reservation.cancelledBy === 'guest'
        ? 'reservations.realtime.guestCancelled'
        : null;
    case 'guest_modified':
      return MODIFIABLE_STATUSES.includes(reservation.status)
        ? 'reservations.realtime.guestModified'
        : null;
  }
}

/** A guest may change a booking many times; every other alert happens once per booking. */
export function isRepeatableReservationAlert(
  trigger: TReservationAlertTrigger
): boolean {
  return trigger === 'guest_modified';
}

/** The booking time, with the day when it is not on today's board. */
export function reservationAlertWhen(
  reservation: Pick<IReservation, 'startsAt' | 'serviceDate'>,
  now: Date,
  timeZone: string,
  businessDayStartHour: number | undefined,
  locale: string
): string {
  const time = formatTime(reservation.startsAt, timeZone, locale);
  return reservation.serviceDate ===
    defaultBoardDate(now, timeZone, businessDayStartHour)
    ? time
    : `${formatDayShort(reservation.startsAt, timeZone, locale)} ${time}`;
}
